// components/PriceMatchCelebration.jsx
//
// The payoff for a successful price match: the price itself counts down from
// what the shopper was about to pay to what they will now pay, inside a short
// burst of confetti.
//
// It renders the price text as well as the effect, which keeps the whole
// animation inside one mounted-then-unmounted component — the page never has to
// run a tween, and React's hook order on the page is untouched.

import { useEffect, useMemo, useRef, useState } from "react";

const COUNTDOWN_MS = 950;
const LIFETIME_MS = 1900;
const PARTICLES = 18;

const reducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * @param {{ from: number | null, to: number, currencySymbol?: string,
 *           onDone?: () => void }} props
 */
export default function PriceMatchCelebration({ from, to, currencySymbol = "$", onDone }) {
  const target = Number(to);
  const start = Number(from);
  const calm = reducedMotion();
  const animate = !calm && Number.isFinite(start) && Number.isFinite(target) && start > target;

  const [value, setValue] = useState(animate ? start : target);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // Each piece gets its own angle, distance, spin and delay, fixed for the life
  // of the burst so a re-render never reshuffles it mid-flight.
  const pieces = useMemo(() => {
    if (calm) return [];
    return Array.from({ length: PARTICLES }, (_, i) => ({
      id: i,
      tone: i % 3,
      style: {
        "--ob-pa": `${(360 / PARTICLES) * i + (Math.random() * 16 - 8)}deg`,
        "--ob-pd": `${52 + Math.random() * 54}px`,
        "--ob-ps": `${5 + Math.random() * 4}px`,
        "--ob-pr": `${Math.random() * 540 - 270}deg`,
        "--ob-pdelay": `${Math.random() * 130}ms`,
      },
    }));
  }, [calm]);

  // Count down on rAF. Never setState after the last frame, and always cancel
  // on unmount — the page can navigate away mid-animation.
  useEffect(() => {
    if (!Number.isFinite(target)) return undefined;
    if (!animate) {
      setValue(target);
      return undefined;
    }
    let raf = 0;
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / COUNTDOWN_MS);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(start + (target - start) * eased);
      if (p < 1) raf = requestAnimationFrame(step);
      else setValue(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [animate, start, target]);

  // One timer owns the end of the celebration, so the caller unmounts us
  // exactly once whatever the browser did with the animations.
  useEffect(() => {
    const t = setTimeout(
      () => onDoneRef.current?.(),
      calm ? 600 : LIFETIME_MS
    );
    return () => clearTimeout(t);
  }, [calm]);

  const shown = Number.isFinite(value) ? value : target;

  return (
    <>
      {currencySymbol}
      {Number.isFinite(shown) ? shown.toFixed(2) : ""}
      <span className="ob-pcel" aria-hidden="true">
        <span className="ob-pcel-glow" />
        <span className="ob-pcel-ring" />
        {pieces.map((p) => (
          <span
            key={p.id}
            className={`ob-pcel-bit ob-pcel-bit-${p.tone}`}
            style={p.style}
          />
        ))}
      </span>
    </>
  );
}
